import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, add } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useAuth } from '../../contexts/auth/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { Event, Resource } from '../admin/scheduling/types';
import CalendarControls from '../admin/scheduling/CalendarControls';
import CalendarView from '../admin/scheduling/CalendarView';
import AppointmentDetailsModal from '../admin/scheduling/AppointmentDetailsModal';
import EventModal from '../admin/scheduling/EventModal';

const CrewScheduleSystem: React.FC = () => {
  const { userProfile } = useAuth();
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [view, setView] = useState<'day' | 'week'>('week');
  const [isAppointmentDetailsModalOpen, setIsAppointmentDetailsModalOpen] = useState<boolean>(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState<boolean>(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Event | null>(null);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [modalDefaultDate, setModalDefaultDate] = useState<Date | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [staffMember, setStaffMember] = useState<{id: string, name: string} | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get current crew member's team record
  useEffect(() => {
    const fetchCrewStaffRecord = async () => {
      if (!userProfile?.id) return;

      const { data, error } = await supabase
        .from('team')
        .select('id, name')
        .eq('user_id', userProfile.id)
        .single();

      if (data && !error) {
        setStaffMember(data);
        // Create single resource for this crew member
        setResources([{
          id: 1,
          name: data.name,
          staffId: data.id
        }]);
      } else {
        console.error('Error fetching crew staff record:', error);
      }
    };

    fetchCrewStaffRecord();
  }, [userProfile]);

  // Fetch appointments for this crew member only
  const fetchAppointments = useCallback(async () => {
    if (!staffMember) return;

    setIsLoading(true);
    const weekStart = startOfWeek(currentDate, { locale: enUS });
    const weekEnd = endOfWeek(currentDate, { locale: enUS });

    try {
      // Fetch both appointments and blocked times in parallel
      const [appointmentsResponse, availabilityResponse] = await Promise.all([
        supabase
          .from('appointments')
          .select(`
            *,
            user_profiles(full_name, preferred_name, email, phone_number),
            appointment_services(
              id,
              service_id,
              price,
              duration,
              services(id, name)
            )
          `)
          .eq('staff_id', staffMember.id)
          .gte('start_time', weekStart.toISOString())
          .lte('end_time', weekEnd.toISOString()),
        supabase
          .from('staff_availability')
          .select('*')
          .eq('staff_id', staffMember.id)
          .gte('specific_date', format(weekStart, 'yyyy-MM-dd'))
          .lte('specific_date', format(weekEnd, 'yyyy-MM-dd'))
      ]);

      if (appointmentsResponse.error) {
        console.error('Error fetching appointments:', appointmentsResponse.error);
        return;
      }

      let allDisplayEvents: Event[] = [];

      // Process appointments into events (same format as admin)
      const activeAppointments = (appointmentsResponse.data || []).filter(apt => apt.status !== 'cancelled');
      const processedEvents: Event[] = activeAppointments.map(apt => {
        // @ts-ignore
        const clientProfile = apt.user_profiles;
        const clientName = clientProfile?.preferred_name || clientProfile?.full_name || 'Guest';
        // @ts-ignore
        const services = apt.appointment_services || [];
        const serviceNames = services.map((s: any) => s.services?.name).filter(Boolean);

        // Add 'Z' to timestamps if missing (matches admin approach)
        const startTimeUTC = apt.start_time.endsWith('Z') ? apt.start_time : apt.start_time + 'Z';
        const endTimeUTC = apt.end_time.endsWith('Z') ? apt.end_time : apt.end_time + 'Z';

        return {
          id: apt.id,
          title: `${clientName} - ${serviceNames.join(', ')}`,
          start: new Date(startTimeUTC).toISOString(),
          end: new Date(endTimeUTC).toISOString(),
          resourceId: 1, // Single resource for crew view
          client: clientName,
          status: apt.status,
          notes: apt.notes || '',
          recurring: false,
          services: services.map((s: any) => ({
            name: s.services?.name || '',
            price: s.price || 0,
            duration: s.duration || 0
          })),
          clientEmail: clientProfile?.email || '',
          clientPhone: clientProfile?.phone_number || '',
          paymentStatus: apt.payment_status,
          appointmentSource: apt.source,
          totalAmount: services.reduce((sum: number, s: any) => sum + (s.price || 0), 0)
        };
      });
      allDisplayEvents = allDisplayEvents.concat(processedEvents);

      // Process blocked times
      if (!availabilityResponse.error && availabilityResponse.data) {
        const processedBlockedTimes = availabilityResponse.data.map(block => {
          return {
            id: block.id,
            title: block.notes || 'Blocked Time',
            start: new Date(`${block.specific_date}T${block.start_time}Z`).toISOString(),
            end: new Date(`${block.specific_date}T${block.end_time}Z`).toISOString(),
            resourceId: 1,
            client: '',
            status: 'cancelled' as Event['status'],
            notes: block.notes || '',
            recurring: block.is_recurring || false,
            isBlockedTime: true,
          };
        });
        allDisplayEvents = allDisplayEvents.concat(processedBlockedTimes);
      }

      setEvents(allDisplayEvents);
    } catch (err) {
      console.error('Error in fetchAppointments:', err);
    } finally {
      setIsLoading(false);
    }
  }, [staffMember, currentDate]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Setup realtime subscription for appointment changes
  useEffect(() => {
    if (!staffMember?.id) return;

    const channel = supabase
      .channel('crew-schedule-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments',
          filter: `staff_id=eq.${staffMember.id}`
        },
        (payload) => {
          // Refresh calendar when any change occurs
          fetchAppointments();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [staffMember, fetchAppointments]);

  // Listen for appointment detail view requests
  useEffect(() => {
    const handleViewAppointmentDetails = (e: any) => {
      const event = e.detail;
      setSelectedAppointment(event);
      setIsAppointmentDetailsModalOpen(true);
    };

    window.addEventListener('view-appointment-details', handleViewAppointmentDetails);

    return () => {
      window.removeEventListener('view-appointment-details', handleViewAppointmentDetails);
    };
  }, []);

  // Handle canceling an appointment
  const handleCancelAppointment = async (eventId: string | number) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status: 'cancelled' })
        .eq('id', eventId);

      if (error) {
        console.error('Error canceling appointment:', error);
        alert('Failed to cancel appointment');
        return;
      }

      // Remove from local state
      setEvents(prev => prev.filter(e => e.id !== eventId));
      alert('Appointment canceled successfully');
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to cancel appointment');
    }
  };

  // Handle editing an appointment
  const handleEditAppointment = () => {
    if (selectedAppointment) {
      setCurrentEvent(selectedAppointment);
      setIsAppointmentDetailsModalOpen(false);
      setIsEventModalOpen(true);
    }
  };

  // Handle opening modal to add blocked time
  const handleOpenEventModal = (dateForModal: Date | null = null): void => {
    setCurrentEvent(null);
    setModalDefaultDate(dateForModal);
    setIsEventModalOpen(true);
  };

  // Handle new blocked time created
  const handleEventCreated = (newEvent: Event) => {
    setEvents(prev => [...prev, newEvent]);
    setIsEventModalOpen(false);
    setModalDefaultDate(null);
    // Refresh to get accurate data
    fetchAppointments();
  };

  // Handle appointment update from EventModal
  const handleAppointmentUpdated = (updatedEvent: Event) => {
    setEvents(prev => prev.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    setIsEventModalOpen(false);
    setCurrentEvent(null);
    // Refresh data
    if (staffMember) {
      fetchAppointments();
    }
  };

  // Handle deleting blocked time
  const handleDeleteEvent = async (eventId: string | number): Promise<void> => {
    try {
      const eventToDelete = events.find(e => e.id === eventId);
      if (!eventToDelete) return;

      // Optimistically remove from UI
      setEvents(prev => prev.filter(e => e.id !== eventId));

      // Delete from database
      if (eventToDelete.isBlockedTime) {
        await supabase
          .from('staff_availability')
          .delete()
          .eq('id', eventId);
      } else {
        // For appointments, just cancel them
        await supabase
          .from('appointments')
          .update({ status: 'cancelled' })
          .eq('id', eventId);
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      // Refresh on error to get accurate state
      fetchAppointments();
    }
  };

  const handlePrevWeek = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };

  const handleNextWeek = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Format date range for display
  const formatDateRange = () => {
    if (view === 'day') {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    } else {
      const weekStart = startOfWeek(currentDate, { locale: enUS });
      const weekEnd = endOfWeek(currentDate, { locale: enUS });
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
    }
  };

  // Get week days for week view
  const getWeekDays = () => {
    const weekStart = startOfWeek(currentDate, { locale: enUS });
    return eachDayOfInterval({
      start: weekStart,
      end: add(weekStart, { days: 6 })
    });
  };

  const weekDays = getWeekDays();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#B2D8C5] border-t-[#3A5E63] rounded-full animate-spin mb-4"></div>
          <p className="text-[#344854]">Loading schedule...</p>
        </div>
      </div>
    );
  }

  if (!staffMember) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          Unable to load your staff profile. Please contact your administrator.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-2">
      {/* Header with add blocked time button */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold text-gray-800">My Schedule</h1>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{staffMember.name}</span>
          </div>
          <button
            onClick={() => handleOpenEventModal()}
            className="px-4 py-2 bg-[#3A5E63] text-white rounded-md hover:bg-[#2d4a4e] transition-colors"
          >
            + Block Time
          </button>
        </div>
      </div>

      {/* Calendar Controls Container */}
      <div className="bg-white rounded-lg shadow p-4">
        <CalendarControls
          currentDate={currentDate}
          view={view}
          formatDateRange={formatDateRange}
          onNavigatePrev={handlePrevWeek}
          onNavigateToday={handleToday}
          onNavigateNext={handleNextWeek}
          onSetView={setView}
        />

        <CalendarView
          view={view}
          weekDays={weekDays}
          currentDate={currentDate}
          events={events}
          onOpenEventModal={handleOpenEventModal}
          onDeleteEvent={handleDeleteEvent}
        />
      </div>

      {/* Appointment Details Modal */}
      <AppointmentDetailsModal
        isOpen={isAppointmentDetailsModalOpen}
        onClose={() => {
          setIsAppointmentDetailsModalOpen(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onEdit={handleEditAppointment}
        onDelete={handleCancelAppointment}
      />

      {/* Event Modal for Creating/Editing Blocked Time and Appointments */}
      <EventModal
        isOpen={isEventModalOpen}
        onClose={() => {
          setIsEventModalOpen(false);
          setCurrentEvent(null);
          setModalDefaultDate(null);
        }}
        currentEvent={currentEvent}
        resources={resources}
        modalDefaultDate={modalDefaultDate}
        onEventCreated={handleEventCreated}
        onEventUpdated={handleAppointmentUpdated}
        allEvents={events}
      />
    </div>
  );
};

export default CrewScheduleSystem;

